import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Switch,
  Text,
  useToast,
  VStack,
  HStack,
  Image,
  Badge,
  ScaleFade,
} from '@chakra-ui/react';
import { COLORS, GRADIENTS } from '../utils/constants';
import useAuth from '../store/useAuth';
import useCredits from '../store/useCredits';
import WalletLinkingInterface from '../components/WalletLinkingInterface';

function Settings() {
  const [apiKey, setApiKey] = useState('');
  const [xummAddress, setXummAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [enableNotifications, setEnableNotifications] = useState(true);
  const [enableDarkMode, setEnableDarkMode] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  
  const navigate = useNavigate();
  const toast = useToast();
  const { user, isLoggedIn } = useAuth();
  const { balance } = useCredits();

  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Redirect to login if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  // Simulate loading saved settings
  useEffect(() => {
    // In a real app, this would load from an API or local storage
    const loadSettings = async () => {
      // Simulated loading delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // This would typically come from your backend
      const savedApiKey = localStorage.getItem('api_key') || '';
      setApiKey(savedApiKey);
      
      // Simulate connected wallet (in a real app, you'd check if user has connected wallet)
      const savedAddress = localStorage.getItem('xumm_address') || '';
      setXummAddress(savedAddress);
      setIsWalletConnected(Boolean(savedAddress));
    };
    
    loadSettings();
  }, []);

  const handleSaveApiKey = () => {
    setIsLoading(true);
    
    // Validate API key (basic validation)
    if (!apiKey.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a valid API key',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
      return;
    }

    try {
      // Save API key to local storage (in a real app, this would be to your backend)
      localStorage.setItem('api_key', apiKey);
      
      toast({
        title: 'Settings saved',
        description: 'Your settings have been saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Simulate save delay
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  const handleConnectWallet = async () => {
    setIsLoading(true);
    
    try {
      // This would be your XAMAN wallet connection logic
      // For demo purposes, we'll simulate a successful connection
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulated wallet address (this would come from the XAMAN SDK)
      const mockWalletAddress = 'r9LpS5ZzPMNVvucKCiPfRbvMZ4uVRdqP3E';
      setXummAddress(mockWalletAddress);
      setIsWalletConnected(true);
      localStorage.setItem('xumm_address', mockWalletAddress);
      
      toast({
        title: 'Wallet connected',
        description: 'Your XAMAN wallet has been successfully connected',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Connection failed',
        description: error.message || 'Failed to connect XAMAN wallet',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnectWallet = () => {
    setIsWalletConnected(false);
    setXummAddress('');
    localStorage.removeItem('xumm_address');
    
    toast({
      title: 'Wallet disconnected',
      description: 'Your XAMAN wallet has been disconnected',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Container maxW="xl" py={8}>
      <ScaleFade in={isVisible} initialScale={0.95}>
        <VStack spacing={8} align="stretch">
          <Flex justify="space-between" align="center">
            <Box>
              <Flex align="center" mb={2}>
                <Image 
                  src="/IgnitaLogo.svg" 
                  alt="Ignita Logo" 
                  height="40px"
                  mr={3}
                />
                <Heading 
                  as="h1" 
                  size="xl" 
                  bgGradient={GRADIENTS.primary}
                  bgClip="text"
                  className="glow-text"
                  letterSpacing="tight"
                  fontFamily="Space Grotesk"
                >
                  Settings
                </Heading>
              </Flex>
              <Text color={COLORS.textSecondary}>
                Customize your Ignita experience
              </Text>
            </Box>
            
            <Badge 
              px={4} 
              py={2} 
              borderRadius="md" 
              fontWeight="bold"
              bg="rgba(0, 168, 107, 0.15)"
              border="1px solid"
              borderColor={COLORS.primary}
              color={COLORS.accent}
              className="pulse"
            >
              Credits: {balance}
            </Badge>
          </Flex>
          
          {/* API Key Settings */}
          <Box 
            borderRadius="lg"
            overflow="hidden"
            bg="rgba(0, 0, 0, 0.6)"
            p={6}
            borderWidth="1px"
            borderColor={COLORS.primary}
            className="glow-border"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
          >
            <Heading size="md" mb={4} className="glow-text">API Settings</Heading>
            
            <FormControl mb={4}>
              <FormLabel color={COLORS.textSecondary}>API Key</FormLabel>
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your OpenAI API Key"
                bg="rgba(0, 0, 0, 0.2)"
                border="1px solid"
                borderColor="rgba(0, 168, 107, 0.3)"
                _hover={{ borderColor: COLORS.accent }}
                _focus={{ 
                  borderColor: COLORS.accent,
                  boxShadow: `0 0 0 1px ${COLORS.accent}`
                }}
                color={COLORS.text}
              />
              <Text fontSize="sm" color={COLORS.textSecondary} mt={1}>
                Your API key is encrypted and stored securely
              </Text>
            </FormControl>
            
            <Button
              onClick={handleSaveApiKey}
              isLoading={isLoading}
              loadingText="Saving..."
              className="primary-button"
            >
              Save API Settings
            </Button>
          </Box>
          
          {/* Wallet Settings */}
          <Box 
            borderRadius="lg"
            overflow="hidden"
            bg="rgba(0, 0, 0, 0.6)"
            p={6}
            borderWidth="1px"
            borderColor={COLORS.primary}
            className="glow-border"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
          >
            <Heading size="md" mb={4} className="glow-text">XAMAN Wallet</Heading>
            
            {isWalletConnected ? (
              <>
                <Flex 
                  align="center" 
                  bg="rgba(30, 220, 11, 0.1)" 
                  p={4} 
                  borderRadius="md"
                  mb={4}
                  borderWidth="1px"
                  borderColor="rgba(30, 220, 11, 0.3)"
                >
                  <Box 
                    bg={COLORS.accent} 
                    p={2}
                    borderRadius="full"
                    color="black"
                    fontSize="xl"
                    mr={3}
                  >
                    ✓
                  </Box>
                  <Box flex="1">
                    <Text fontWeight="bold" color={COLORS.text}>Connected Wallet</Text>
                    <Text fontSize="sm" color={COLORS.textSecondary}>
                      {xummAddress.slice(0, 8)}...{xummAddress.slice(-8)}
                    </Text>
                  </Box>
                </Flex>
                
                <Button
                  onClick={handleDisconnectWallet}
                  className="outline-button"
                >
                  Disconnect Wallet
                </Button>
              </>
            ) : (
              <>
                <Text color={COLORS.textSecondary}>
                  Connect your XAMAN wallet to enable XGCL payments for AI image generation.
                </Text>
                
                <Button
                  onClick={handleConnectWallet}
                  isLoading={isLoading}
                  loadingText="Connecting..."
                  className="outline-button"
                  leftIcon={
                    <Box boxSize="24px" mr={2} className="pulse">
                      <Box 
                        bg={COLORS.accent} 
                        p={1} 
                        borderRadius="full" 
                        boxSize="24px"
                        opacity={0.9}
                      ></Box>
                    </Box>
                  }
                >
                  Connect XAMAN Wallet
                </Button>
              </>
            )}
          </Box>
          
          {/* Account Wallet Linking - Only show for email/Google users without connected wallet */}
          {user && user.email && !user.hasConnectedWallet && (
            <Box 
              borderRadius="lg"
              overflow="hidden"
              bg="rgba(0, 0, 0, 0.6)"
              p={6}
              borderWidth="1px"
              borderColor={COLORS.primary}
              className="glow-border"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
              backdropFilter="blur(10px)"
            >
              <Heading size="md" mb={4} className="glow-text">Link XRPL Wallet</Heading>
              <Text color={COLORS.textSecondary} mb={4}>
                Link your XRPL wallet to enable blockchain payments and enhanced features.
              </Text>
              
              <WalletLinkingInterface 
                onLinkSuccess={() => {
                  toast({
                    title: 'Wallet Linked!',
                    description: 'Your XRPL wallet has been successfully linked to your account.',
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                  });
                  // Refresh page to update UI
                  window.location.reload();
                }}
              />
            </Box>
          )}
          
          {/* Preferences */}
          <Box 
            borderRadius="lg"
            overflow="hidden"
            bg="rgba(0, 0, 0, 0.6)"
            p={6}
            borderWidth="1px"
            borderColor={COLORS.primary}
            className="glow-border"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
          >
            <Heading size="md" mb={4} className="glow-text">Preferences</Heading>
            
            <VStack spacing={4} align="stretch">
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontWeight="medium" color={COLORS.text}>Dark Mode</Text>
                  <Text fontSize="sm" color={COLORS.textSecondary}>
                    Toggle between dark and light theme
                  </Text>
                </Box>
                <Switch 
                  isChecked={enableDarkMode}
                  onChange={() => setEnableDarkMode(!enableDarkMode)}
                  colorScheme="green"
                />
              </Flex>
              
              <Divider borderColor="rgba(30, 220, 11, 0.3)" />
              
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontWeight="medium" color={COLORS.text}>Notifications</Text>
                  <Text fontSize="sm" color={COLORS.textSecondary}>
                    Receive alerts about new features and updates
                  </Text>
                </Box>
                <Switch 
                  isChecked={enableNotifications}
                  onChange={() => setEnableNotifications(!enableNotifications)}
                  colorScheme="green"
                />
              </Flex>
            </VStack>
          </Box>
          
          {/* Account Section */}
          <Box 
            borderRadius="lg"
            overflow="hidden"
            bg="rgba(0, 0, 0, 0.6)"
            p={6}
            borderWidth="1px"
            borderColor={COLORS.primary}
            className="glow-border"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
          >
            <Heading size="md" mb={4} className="glow-text">Account</Heading>
            
            <HStack spacing={4} mb={4}>
              <Button
                className="outline-button"
                onClick={() => navigate('/profile')}
              >
                Edit Profile
              </Button>
              
              <Button
                variant="outline"
                borderColor="red.500"
                color="red.400"
                _hover={{
                  bg: "rgba(255, 0, 0, 0.1)",
                  borderColor: "red.300",
                  color: "red.300"
                }}
              >
                Delete Account
              </Button>
            </HStack>
            
            <Text fontSize="sm" color={COLORS.textSecondary}>
              Account deletion will permanently remove all your data including generated images.
            </Text>
          </Box>
        </VStack>
      </ScaleFade>
    </Container>
  );
}

export default Settings; 